import { useApiAction } from "api/drops/useApiAction";
import {
  userGetTelephoneApi,
  userLogOnOffTelephoneApi,
} from "api/puzzel/puzzel";
import { usePuzzelAuthenticationContext } from "features/Header/PuzzelTelephone/provider/contexts/PuzzelAuthenticationContext";
import { useUserValidationState } from "features/Header/PuzzelTelephone/provider/contexts/UserValidationContext";
import React, { FC, ReactNode, useEffect } from "react";

type UserDetails = {
  currentProfile: any;
  profiles: any;
  isLoggedOn: boolean | null;
};

type CallStatusValue = {
  userDetails: UserDetails;
  performUserLogOnOff: (args: any) => void;
};

const CallStatusContext = React.createContext<CallStatusValue | undefined>(
  undefined,
);

function useCallStatus(): CallStatusValue {
  const { userId } = useUserValidationState();

  const { isAuthenticated } = usePuzzelAuthenticationContext();

  const [userGetRequest, performUserGet]: any = useApiAction(
    userGetTelephoneApi,
    false,
  );

  const [userLogOnOffRequest, performUserLogOnOff] = useApiAction(
    userLogOnOffTelephoneApi,
    false,
  );

  useEffect(() => {
    if (isAuthenticated && userId) {
      performUserGet({ userId });
    }
  }, [isAuthenticated, userLogOnOffRequest.data, userId]);

  const profileId = userGetRequest.data?.result?.currentProfileId;
  const currentProfile = profileId
    ? userGetRequest.data?.result?.profiles.find(
        (profile: { id: number }) => profile.id === profileId,
      )
    : null;
  const profiles = userGetRequest.data?.result?.profiles;

  const statusLogonFromLogOnOffRequest = userLogOnOffRequest.data?.result
    ? userLogOnOffRequest.data?.result !== "LoggedOff"
    : undefined;
  const statusLoginFromUserDetailsRequest = userGetRequest.data?.result
    ?.contactCentreStatus
    ? userGetRequest.data?.result?.contactCentreStatus !== "LoggedOff"
    : undefined;

  let isLoggedOn: boolean | null = null;
  if (statusLogonFromLogOnOffRequest !== undefined) {
    isLoggedOn = statusLogonFromLogOnOffRequest;
  } else if (statusLoginFromUserDetailsRequest !== undefined) {
    isLoggedOn = statusLoginFromUserDetailsRequest;
  }

  return {
    userDetails: {
      currentProfile,
      profiles,
      isLoggedOn,
    },
    performUserLogOnOff,
  };
}

const UserDetailsAndLogOnOffProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const value = useCallStatus();
  return (
    <CallStatusContext.Provider value={value}>
      {children}
    </CallStatusContext.Provider>
  );
};

function useCallStatusContext(): CallStatusValue {
  const context = React.useContext(CallStatusContext);
  if (context === undefined) {
    throw new Error(
      "useUserDetailsState must be used within a UserDetailsAndLogOnOffProvider",
    );
  }
  return context;
}

function useUserDetailsState(): UserDetails {
  return useCallStatusContext().userDetails;
}

function usePerformUserLogOnOff(): (args: any) => void {
  return useCallStatusContext().performUserLogOnOff;
}

export {
  usePerformUserLogOnOff,
  UserDetailsAndLogOnOffProvider,
  useUserDetailsState,
};
